import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Flex, Text, useStyleConfig} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../../utils/contentstack-utils'
import {ChevronRight} from '../../../icons'

const COLOR_MAP = {
    grey: {
        bg: 'background.base.under',
        bgOpacity: 0.78,
        titleColor: 'foreground.neutral.default',
        subtitleColor: 'foreground.neutral.default',
        ctaColor: 'foreground.neutral.default',
    },
    blue: {
        bg: 'background.strong.muted',
        bgOpacity: 0.72,
        titleColor: 'foreground.accent.default',
        subtitleColor: 'foreground.accent.default',
        ctaColor: 'foreground.neutral.default',
    },
    red: {
        bg: 'background.negative.muted',
        bgOpacity: 0.72,
        titleColor: 'foreground.negative.default',
        subtitleColor: 'foreground.negative.default',
        ctaColor: 'foreground.neutral.default',
    },
}

const BentoCard = ({card, index, categoriesData, productsData, locale, buildUrl}) => {
    const styles = useStyleConfig('HeroBanner')
    const {url, isAbsolute} = getGlobalLink({
        link: card?.redirect,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })

    const colorKey = card?.card_color?.toLowerCase()
    const colors = COLOR_MAP[colorKey] || COLOR_MAP.grey
    const sharedProps = {
        ...styles.v2BentoCard,
        'data-group': '',
        backgroundColor: colors.bg,
        opacity: colors.bgOpacity,
        sx: {
            WebkitBackdropFilter: 'blur(12px)',
            '&:hover [data-bento-card-cta], &:focus-visible [data-bento-card-cta]': {
                opacity: 1,
            },
        },
        flex: {base: '1 1 auto', lg: index === 0 ? '2 1 0%' : '1 1 0%'},
    }

    const content = (
        <>
            <Flex {...styles.v2BentoCardContent}>
                {card?.icon?.url && (
                    <Box
                        aria-hidden="true"
                        {...styles.v2BentoIcon}
                        backgroundColor={colors.titleColor}
                        sx={{
                            maskImage: `url(${card.icon.url})`,
                            WebkitMaskImage: `url(${card.icon.url})`,
                            maskRepeat: 'no-repeat',
                            WebkitMaskRepeat: 'no-repeat',
                            maskPosition: 'center',
                            WebkitMaskPosition: 'center',
                            maskSize: 'contain',
                            WebkitMaskSize: 'contain',
                        }}
                    />
                )}
                <Flex {...styles.v2BentoTextRow}>
                    <Flex {...styles.v2BentoTitleGroup}>
                        {card?.title_text && (
                            <Text {...styles.v2BentoTitle} color={colors.titleColor}>
                                {card.title_text}
                            </Text>
                        )}
                        {card?.subtitle_text && (
                            <Text {...styles.v2BentoSubtitle} color={colors.subtitleColor}>
                                {card.subtitle_text}
                            </Text>
                        )}
                    </Flex>

                    {url && (
                        <Button
                            as="span"
                            {...styles.v2BentoCta}
                            data-bento-card-cta
                            display="inline-flex"
                            opacity={{base: 1, lg: 0}}
                            transition="opacity 0.2s ease"
                            aria-label={card?.redirect?.cta?.title || card?.title_text || 'Explore'}
                            color={colors.ctaColor}
                            borderColor={colors.ctaColor}
                            _groupHover={{opacity: 1}}
                            _groupFocusVisible={{opacity: 1}}
                        >
                            <ChevronRight boxSize="iconsSize.small" />
                        </Button>
                    )}
                </Flex>
            </Flex>
        </>
    )

    if (!url) {
        return <Box {...sharedProps}>{content}</Box>
    }

    if (isAbsolute) {
        return (
            <Box
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                textDecoration="none"
                _hover={{textDecoration: 'none'}}
                {...sharedProps}
            >
                {content}
            </Box>
        )
    }

    return (
        <Box
            as={Link}
            to={safeBuildUrl(url, buildUrl)}
            textDecoration="none"
            _hover={{textDecoration: 'none'}}
            {...sharedProps}
        >
            {content}
        </Box>
    )
}

BentoCard.propTypes = {
    card: PropTypes.shape({
        card_color: PropTypes.string,
        title_text: PropTypes.string,
        subtitle_text: PropTypes.string,
        icon: PropTypes.shape({
            url: PropTypes.string,
        }),
        redirect: PropTypes.object,
    }),
    index: PropTypes.number,
    categoriesData: PropTypes.object,
    productsData: PropTypes.object,
    locale: PropTypes.object,
    buildUrl: PropTypes.func,
}

export const HeroBannerV2BentoBox = ({cards = [], bentobox}) => {
    const {buildUrl, locale} = useMultiSite()
    const categoriesData = useGetCategoriesData()
    const styles = useStyleConfig('HeroBanner')

    const resolvedCards = Array.isArray(cards)
        ? cards
        : Array.isArray(bentobox?.card)
        ? bentobox.card
        : []

    const productIds = resolvedCards
        .filter((card) => card?.redirect?.productsfcc?.data?.length > 0)
        .map((card) => card.redirect.productsfcc.data[0].id)
        .filter(Boolean)

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'hero-banner-v2-bento-box',
            },
        },
        {
            enabled: productIds.length > 0,
        },
    )

    if (!resolvedCards.length) return null

    return (
        <Flex data-testid="hero-banner-v2-bento-box" {...styles.v2BentoBox}>
            {resolvedCards.map((card, index) => (
                <BentoCard
                    key={card?._metadata?.uid || card?.title_text}
                    card={card}
                    index={index}
                    categoriesData={categoriesData}
                    productsData={productsData}
                    locale={locale}
                    buildUrl={buildUrl}
                />
            ))}
        </Flex>
    )
}

HeroBannerV2BentoBox.propTypes = {
    cards: PropTypes.arrayOf(PropTypes.object),
    bentobox: PropTypes.shape({
        card: PropTypes.arrayOf(PropTypes.object),
    }),
}

export default HeroBannerV2BentoBox
